import axios from 'axios';
import { expireSession } from './utils/auth';
import { isLogoutExempt, isRoleRefusal } from './utils/logoutExemptions';

/**
 * Global axios interceptor — attaches the Bearer token from localStorage on every request.
 *
 * The login flow stores the SignIn response under `localStorage.user`:
 *   { user: { ...userFields, token }, token }
 *
 * Either `localStorage.user.token` or `localStorage.user.user.token` may be present
 * depending on backend version. Read both for resilience.
 */
function getStoredToken(): string | null {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.token || parsed?.user?.token || null;
  } catch {
    return null;
  }
}

axios.interceptors.request.use((req) => {
  const token = getStoredToken();
  if (token) {
    req.headers = req.headers || {};
    if (!req.headers.Authorization) {
      req.headers.Authorization = `Bearer ${token}`;
    }
  }
  return req;
});

// Which endpoints treat 401/403 as a NORMAL signal (and so must not trigger
// the auto-logout) is defined and documented in utils/logoutExemptions.ts.
axios.interceptors.response.use(
  (resp) => resp,
  (err) => {
    const reqUrl = err?.config?.url || '';
    const isAuthSignalCall = isLogoutExempt(reqUrl) || isRoleRefusal(err?.response?.data);
    if (!isAuthSignalCall && (err?.response?.status === 401 || err?.response?.status === 403)) {
      console.warn('[auth] received', err.response.status, '— clearing session and redirecting to login');
      // Stash-return-path + expired notice + clear + redirect. The redirect
      // is skipped when already on an unauthenticated page (no loop).
      expireSession();
    }
    return Promise.reject(err);
  }
);
